import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Badge,
  Spinner,
  Alert,
  Button,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/TableContainer';
import InvoicePrintContent from '../../components/Invoices/InvoicePrintContent';
import { InvoiceId, OrderId, DownloadPdf, InvoiceListRow } from './invoicelistCol';
import {
  GET_ORDER,
  GetOrderData,
  GetOrderVariables,
  OrderNode,
  SingleOrder,
} from '../../graphql/queries/orders';
import { buildInvoiceNumberMap } from '../../utils/invoiceNumber';
import { formatOrderTotal } from '../../utils/orderLineItemMeta';
import { apolloClient } from '../../helpers/graphql_client';
import { useScopedOrders } from '../../hooks/useScopedOrders';

const statusBadgeColor: Record<string, string> = {
  PROCESSING: 'success',
  COMPLETED: 'success',
  ON_HOLD: 'warning',
  PENDING: 'secondary',
  CANCELLED: 'danger',
  REFUNDED: 'info',
  FAILED: 'danger',
  'CANCEL REQUEST': 'secondary',
};

function getStatusColor(status: string): string {
  if (!status) return 'secondary';
  const upper = status.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
  return statusBadgeColor[upper] || 'secondary';
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getBillingName(row: OrderNode): string {
  if (row.billing?.firstName || row.billing?.lastName) {
    return [row.billing.firstName, row.billing.lastName].filter(Boolean).join(' ');
  }
  if (row.customer?.firstName || row.customer?.lastName) {
    return [row.customer.firstName, row.customer.lastName].filter(Boolean).join(' ');
  }
  return row.billing?.email || row.customer?.email || '—';
}

const InvoiceList = () => {
  document.title = 'Invoice List | NGK Tours';
  const navigate = useNavigate();
  const [printingOrderId, setPrintingOrderId] = useState<number | null>(null);
  const [printPayload, setPrintPayload] = useState<{
    order: SingleOrder;
    invoiceNumber: string;
  } | null>(null);

  const { orders: fetchedOrders, loading, error, refetch } = useScopedOrders({
    first: 100,
    paginateAll: true,
  });

  const orders = useMemo((): InvoiceListRow[] => {
    const nodes = [...fetchedOrders];
    const invoiceMap = buildInvoiceNumberMap(nodes);

    return nodes
      .map((order) => ({
        ...order,
        invoiceNumber: invoiceMap.get(order.databaseId) ?? '—',
      }))
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        if (dateB !== dateA) return dateB - dateA;
        const numA = Number(a.orderNumber ?? a.databaseId);
        const numB = Number(b.orderNumber ?? b.databaseId);
        return numB - numA;
      });
  }, [fetchedOrders]);

  const showInitialSpinner = loading && fetchedOrders.length === 0;
  const showPagingSpinner = loading && fetchedOrders.length > 0;

  const handlePrintPdf = useCallback(async (row: InvoiceListRow) => {
    setPrintingOrderId(row.databaseId);
    try {
      const { data: orderData } = await apolloClient.query<GetOrderData, GetOrderVariables>({
        query: GET_ORDER,
        variables: { id: String(row.databaseId), idType: 'DATABASE_ID' },
        fetchPolicy: 'network-only',
      });
      if (!orderData?.order) return;
      setPrintPayload({
        order: orderData.order,
        invoiceNumber: row.invoiceNumber,
      });
    } finally {
      setPrintingOrderId(null);
    }
  }, []);

  useEffect(() => {
    if (!printPayload) return;

    document.body.classList.add('invoice-print-active');

    const timer = window.setTimeout(() => {
      window.print();
    }, 400);

    const onAfterPrint = () => {
      document.body.classList.remove('invoice-print-active');
      setPrintPayload(null);
    };

    window.addEventListener('afterprint', onAfterPrint, { once: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', onAfterPrint);
      document.body.classList.remove('invoice-print-active');
    };
  }, [printPayload]);

  const columns = useMemo(
    () => [
      {
        Header: 'Invoice ID',
        accessor: 'invoiceNumber',
        filterable: true,
        disableFilters: true,
        sortable: true,
        Cell: (cellProps: { row: { original: InvoiceListRow } }) => (
          <InvoiceId row={cellProps.row} />
        ),
      },
      {
        Header: 'Order ID',
        accessor: 'orderNumber',
        filterable: true,
        disableFilters: true,
        sortable: true,
        Cell: (cellProps: { row: { original: InvoiceListRow } }) => (
          <OrderId row={cellProps.row} />
        ),
      },
      {
        Header: 'Date',
        accessor: 'date',
        filterable: true,
        disableFilters: true,
        sortable: true,
        Cell: (cellProps: { row: { original: InvoiceListRow } }) =>
          formatDate(cellProps.row.original.date),
      },
      {
        Header: 'Billing Name',
        accessor: 'billingName',
        filterable: true,
        disableFilters: true,
        sortable: true,
        Cell: (cellProps: { row: { original: InvoiceListRow } }) =>
          getBillingName(cellProps.row.original),
      },
      {
        Header: 'Amount',
        id: 'total',
        accessor: (row: InvoiceListRow) => formatOrderTotal(row.total, row.currency),
        filterable: true,
        sortable: true,
        disableFilters: true,
      },
      {
        Header: 'Status',
        accessor: 'status',
        filterable: true,
        sortable: true,
        disableFilters: true,
        Cell: (cellProps: { row: { original: InvoiceListRow } }) => {
          const status = cellProps.row.original.status || '—';
          return <Badge className={`bg-${getStatusColor(status)}`}>{status}</Badge>;
        },
      },
      {
        Header: 'Download Pdf',
        accessor: 'downloadpdf',
        filterable: true,
        sortable: true,
        disableFilters: true,
        Cell: (cellProps: { row: { original: InvoiceListRow } }) => (
          <DownloadPdf
            row={cellProps.row}
            onPrint={handlePrintPdf}
            printing={printingOrderId === cellProps.row.original.databaseId}
          />
        ),
      },
      {
        Header: 'Action',
        Cell: (cellProps: { row: { original: InvoiceListRow } }) => {
          const row = cellProps.row.original;
          return (
            <Button
              color="primary"
              size="sm"
              className="btn-sm"
              onClick={() => navigate(`/invoices-detail/${row.databaseId}`)}
            >
              View
            </Button>
          );
        },
      },
      {
        Header: 'Details',
        Cell: (cellProps: { row: { original: InvoiceListRow } }) => {
          const row = cellProps.row.original;
          return (
            <Button
              color="primary"
              size="sm"
              className="btn-sm"
              onClick={() =>
                navigate(`/orders/${row.databaseId}`, { state: { fromInvoices: true } })
              }
            >
              View Details
            </Button>
          );
        },
      },
    ],
    [navigate, handlePrintPdf, printingOrderId]
  );

  return (
    <React.Fragment>
      {printPayload &&
        createPortal(
          <div className="invoice-print-portal" aria-hidden="true">
            <InvoicePrintContent
              order={printPayload.order}
              invoiceNumber={printPayload.invoiceNumber}
            />
          </div>,
          document.body
        )}
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Invoices" breadcrumbItem="Invoice List" />
          <Row className="mt-0">
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col className="text-end">
                      <Button color="secondary" size="sm" outline onClick={() => refetch()}>
                        Refresh
                      </Button>
                    </Col>
                  </Row>
                  {error && (
                    <Alert color="danger" className="mb-3">
                      {(error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]
                        ?.message || error.message}
                    </Alert>
                  )}
                  {showInitialSpinner ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading invoices...</p>
                    </div>
                  ) : showPagingSpinner ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading all invoices...</p>
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={orders}
                      isGlobalFilter={true}
                      customPageSize={10}
                      className="align-middle table-nowrap"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default InvoiceList;
